import React from 'react';
import PlusIconSmall from '@/app/components/questionnaireV2/icons/plus-icon-small';
import MinusIconSmall from '@/app/components/questionnaireV2/icons/minus-icon-small';
import { AnimatePresence, motion } from 'motion/react';

interface ActivityNumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  stepSize?: number;
}

export const ActivityNumberInput: React.FC<ActivityNumberInputProps> = ({ label, value, onChange, min = 0, max, stepSize = 1 }) => {
  const isMinDisabled = value <= min;
  const isMaxDisabled = max !== undefined && value >= max;

  const handleDecrement = () => {
    if (isMinDisabled) return;
    const newValue = value - stepSize;
    if (newValue >= min) {
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    if (isMaxDisabled) return;
    const newValue = value + stepSize;
    if (!max || newValue <= max) {
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      onChange(min);
      return;
    }

    const newValue = parseInt(inputValue);
    if (!isNaN(newValue)) {
      if (newValue >= min && (!max || newValue <= max)) {
        onChange(newValue);
      }
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      onChange(min);
      return;
    }

    const newValue = parseInt(inputValue) || min;
    if (newValue < min) {
      onChange(min);
    } else if (max && newValue > max) {
      onChange(max);
    } else {
      onChange(newValue);
    }
  };

  return (
    <div className="activity-input-group">
      <label className="activity-input-label">{label}</label>
      <div className="activity-number-input">
        <AnimatePresence>
          <motion.button
            key={`${label}-minus`}
            onClick={handleDecrement}
            disabled={isMinDisabled}
            className="activity-number-input-button"
            aria-label={`Decrease ${label}`}
            whileHover={{ scale: isMinDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isMinDisabled ? 1 : 0.95 }}
            transition={{ duration: 0.15 }}
            type='button'
          >
            <MinusIconSmall />
          </motion.button>

          <div className="activity-number-input-value">
            <input
              type="number"
              value={value === min ? '' : value}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className="activity-number-input-value-input"
              min={min}
              max={max}
              placeholder="0"
            />
          </div>

          <motion.button
            key={`${label}-plus`}
            onClick={handleIncrement}
            disabled={isMaxDisabled}
            className="activity-number-input-button"
            aria-label={`Increase ${label}`}
            whileHover={{ scale: isMaxDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isMaxDisabled ? 1 : 0.95 }}
            transition={{ duration: 0.15 }}
            type='button'
          >
            <PlusIconSmall />
          </motion.button>
        </AnimatePresence>
      </div>
    </div>
  );
};
